import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import agentService from '../../services/agentService';
import usersService from '../../services/usersService';

// Async thunk to save default agent to user preferences
export const saveDefaultAgent = createAsyncThunk(
  'agents/saveDefaultAgent',
  async (agentId, { rejectWithValue }) => {
    try {
      await usersService.preferencesUpdate({
        preferences: {
          defaultAgentId: agentId,
        },
      });
      return agentId;
    } catch (error) {
      console.error('Failed to save default agent:', error);
      // Don't reject - this is a non-critical operation
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch user preferences (for defaultAgentId)
export const fetchUserPreferences = createAsyncThunk('agents/fetchUserPreferences', async () => {
  try {
    const data = await usersService.preferencesGet();
    return data.data?.preferences || data.preferences || {};
  } catch (error) {
    // Don't reject - preferences are optional
    console.warn('Failed to fetch user preferences:', error);
    return {};
  }
});

// Async thunks for API calls
export const fetchAgents = createAsyncThunk(
  'agents/fetchAgents',
  async (_, { rejectWithValue, getState }) => {
    try {
      const data = await agentService.list();
      // API returns data.data.agents (wrapped structure)
      // Return just the agents array
      const agents = data.data?.agents || data.data || [];

      // Try to get defaultAgentId from user preferences (from auth state)
      const state = getState();
      const userPreferences = state.auth?.user?.preferences || {};
      const defaultAgentId = userPreferences.defaultAgentId;

      return { agents, defaultAgentId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAgent = createAsyncThunk(
  'agents/createAgent',
  async (agentData, { rejectWithValue }) => {
    try {
      const response = await agentService.create(agentData);

      // Handle different response structures
      if (!response.success) {
        return rejectWithValue(response.error || response.message || 'Failed to create agent');
      }

      // Response structure: { success: true, data: { agent object } }
      // Or sometimes: { success: true, data: { agent: { agent object } } }
      const agent = response.data?.agent || response.data;

      if (!agent) {
        return rejectWithValue('Agent data not found in response');
      }

      return agent;
    } catch (error) {
      console.error('Create agent error:', error);
      return rejectWithValue(error.message || 'Failed to create agent');
    }
  }
);

export const updateAgent = createAsyncThunk(
  'agents/updateAgent',
  async ({ agentId, agentData }, { rejectWithValue }) => {
    try {
      const data = await agentService.update(agentId, agentData);
      if (data && data.success === false) {
        return rejectWithValue(data.message || data.error || 'Update failed');
      }
      return data.data?.agent || data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAgent = createAsyncThunk(
  'agents/deleteAgent',
  async (agentId, { rejectWithValue }) => {
    try {
      await agentService.remove(agentId);
      return agentId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const testAgent = createAsyncThunk(
  'agents/testAgent',
  async ({ agentId, testData }, { rejectWithValue }) => {
    try {
      const data = await agentService.test(agentId, testData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  agents: [],
  selectedAgent: null,
  loading: false,
  error: null,
  testResults: null,
  testLoading: false,
  testError: null,
};

const agentsSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    selectAgent: (state, action) => {
      state.selectedAgent = action.payload;
      // Note: Saving to preferences is handled by component via saveDefaultAgent thunk
    },
    clearError: state => {
      state.error = null;
    },
    clearTestResults: state => {
      state.testResults = null;
      state.testError = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch agents
      .addCase(fetchAgents.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.loading = false;
        const { agents, defaultAgentId } = action.payload;
        state.agents = agents;

        const byId = agent => agent && (agent._id || agent.id);

        if (state.selectedAgent && agents.length > 0) {
          // Re-resolve selected agent from the new list so we keep the same selection by ID
          const currentId = byId(state.selectedAgent);
          const found = currentId && agents.find(a => byId(a) === currentId);
          if (found) {
            state.selectedAgent = found;
          } else {
            // Selected agent no longer in list (e.g. deleted elsewhere), fall back to default/first
            state.selectedAgent = defaultAgentId
              ? agents.find(a => byId(a) === defaultAgentId) || agents[0]
              : agents[0];
          }
        } else if (!state.selectedAgent && agents.length > 0) {
          // No selection yet: restore from preferences or use first agent
          if (defaultAgentId) {
            const defaultAgent = agents.find(a => byId(a) === defaultAgentId);
            state.selectedAgent = defaultAgent || agents[0];
          } else {
            state.selectedAgent = agents[0];
          }
        }
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create agent
      .addCase(createAgent.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.agents.push(action.payload);
        // Select the newly created agent
        state.selectedAgent = action.payload;
        // Note: Saving to preferences should be done in component after creation
        // to ensure async operation completes properly
      })
      .addCase(createAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update agent
      .addCase(updateAgent.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAgent.fulfilled, (state, action) => {
        state.loading = false;
        const payloadId = action.payload._id || action.payload.id;
        const index = state.agents.findIndex(agent => (agent._id || agent.id) === payloadId);
        if (index !== -1) {
          state.agents[index] = action.payload;
        }
        // Update selected agent if it's the one being updated
        if (
          state.selectedAgent &&
          (state.selectedAgent._id || state.selectedAgent.id) === payloadId
        ) {
          state.selectedAgent = action.payload;
        }
      })
      .addCase(updateAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete agent
      .addCase(deleteAgent.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.agents = state.agents.filter(agent => (agent._id || agent.id) !== deletedId);
        // If deleted agent was selected, select first available agent
        if (
          state.selectedAgent &&
          (state.selectedAgent._id || state.selectedAgent.id) === deletedId
        ) {
          state.selectedAgent = state.agents.length > 0 ? state.agents[0] : null;
        }
      })
      .addCase(deleteAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Test agent
      .addCase(testAgent.pending, state => {
        state.testLoading = true;
        state.testError = null;
      })
      .addCase(testAgent.fulfilled, (state, action) => {
        state.testLoading = false;
        state.testResults = action.payload;
      })
      .addCase(testAgent.rejected, (state, action) => {
        state.testLoading = false;
        state.testError = action.payload;
      });
  },
});

export const { selectAgent, clearError, clearTestResults, setLoading } = agentsSlice.actions;

// Selectors
export const selectAgents = state => state.agents.agents;
export const selectSelectedAgent = state => state.agents.selectedAgent;
export const selectAgentsLoading = state => state.agents.loading;
export const selectAgentsError = state => state.agents.error;
export const selectTestResults = state => state.agents.testResults;
export const selectTestLoading = state => state.agents.testLoading;
export const selectTestError = state => state.agents.testError;

export default agentsSlice.reducer;
